import { useState } from "react";
import { Box, Button, Divider, Popover, Typography } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { CalendarItem, CalendarEvent } from "../calendar.types";
import { useFields } from "../../../api/useFields";
import { renderFieldView } from "../../fieldsViews/renderFieldView";
import { findField } from "../../../utils/findField";
import { SessionStatusField } from "../../../screens/sessions/components/SessionStatusField";
import { SessionActionButtons } from "../../../screens/sessions/components/SessionActionButtons";
import { Session } from "../../../types/records.types";
import { useQueryClient } from "@tanstack/react-query";

type SessionTooltipProps = {
    event: CalendarItem;
    children: React.ReactElement;
};

const TOOLTIP_FIELDS = ["treatment", "start_date", "service", "technician", "clinic_id", "notes"];

type PopoverContentProps = {
    event: CalendarItem;
    onClose: () => void;
};

const PopoverContent = ({ event, onClose }: PopoverContentProps) => {
    const { formatedFields, status } = useFields("sessions");
    const queryClient = useQueryClient();

    const invalidateSessionsQueries = () => {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const queryKey = query.queryKey[0] as string;
                return typeof queryKey === "string" && queryKey.includes("sessions");
            },
        });
        onClose();
    };

    if (status !== "success") return null;

    const statusField = findField(formatedFields, "status");
    const sessionForStatusField = event as unknown as Session;
    const calendarEventForStatusField = {
        id: event.id,
        status: event.status,
    } as CalendarEvent;

    return (
        <Box sx={{ p: 1.5, minWidth: 350 }}>
            <Box sx={{ mb: 2 }}>
                <SessionActionButtons session={event} />
            </Box>
            <Divider sx={{ my: 4 }} />
            {TOOLTIP_FIELDS.map((fieldName) => {
                const field = findField(formatedFields, fieldName);
                const value = (event as any)[fieldName];

                if (!field || !value) return null;

                return (
                    <Box key={fieldName} sx={{ mb: 2 }}>
                        <Typography variant="caption" color="primary">
                            {field.headerName}
                        </Typography>
                        <Box>{renderFieldView(field, { [field.field]: value }, "group")}</Box>
                    </Box>
                );
            })}

            {statusField && (
                <Box sx={{ mb: 4, mt: 4 }}>
                    <SessionStatusField
                        field={statusField}
                        value={event.status}
                        event={calendarEventForStatusField}
                        session={sessionForStatusField}
                        setPreviewIsOpen={onClose}
                        refetchSessions={invalidateSessionsQueries}
                    />
                </Box>
            )}

            <Divider sx={{ my: 4 }} />

            <Button
                size="small"
                variant="contained"
                endIcon={<OpenInNew />}
                href={`/sessions/${event.id}/update`}
                target="_blank"
                fullWidth
            >
                Atualizar Sessão
            </Button>
        </Box>
    );
};

export const SessionTooltip = ({ event, children }: SessionTooltipProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const isOpen = Boolean(anchorEl);

    return (
        <>
            <Box onClick={handleClick}>{children}</Box>
            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: "background.paper",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            padding: 4,
                        },
                    },
                }}
            >
                <PopoverContent event={event} onClose={handleClose} />
            </Popover>
        </>
    );
};
